import store from "../../redux-store";
import { replace } from "../../utils/OfflineUtil";
import { getLocalStorage, setLocalStorage, removeLocalStorage } from "../../utils/localStorage";
import { OFFLINE_DOMAINS, LOCALSTORAGE_KEYS } from "../../utils/constants";

export * from "./shared";

/**
 * Uplifted WHOLE from `apps/web/src/helper/cartAuditHelper.ts` — the cart-audit telemetry log.
 *
 * ⚠️ **WEB-ONLY: there is no `helper/cartAuditHelper` in `apps/mobile`.** Verified with `ls`, not
 * assumed (rule 4 / hazard 32). That is why this folder has only a `.desktop` variant.
 *
 * ⚠️ **This module exports its own `logEvent`, which is NOT the effects bridge's.** The bridge's
 * is a Sentry breadcrumb; this one is `logEvent(level, event, data)`, a local audit logger that
 * writes rows into the offline store. The audit's `REVIEWED_DIVERGENT` registry has carried that
 * name-clash as a standing note — it is now a clash between two CORE modules rather than between
 * core and an app, and importing the wrong one is a real hazard. Always import it from this file
 * by path, never from `effects-bridge`.
 *
 * ⚠️ `cartCreatedAt` is an exported MUTABLE module-level binding, written only through
 * `setCartCreatedAt`. It moved verbatim: making it a getter would change the export's kind, and
 * consumers read it as a value.
 *
 * ⚠️ `store.getState()` / `store.dispatch(` still read exactly that — the `store` is now core's own, imported from `../../redux-store/index.desktop`; nothing else changed.
 */

interface CartAuditEventParams {
  eventName: string;
  cartId: string;
  cartValue: number;
  timestamp?: number;
  staffId?: string;
  staffName?: string;
  channelOverride?: { channelId: string; channelDefId: string; channelName: string };
}

export const LOG_LEVELS = {
  INFO: 'info',
  SUCCESS: 'success',
  WARN: 'warn',
  ERROR: 'error',
  SKIP: 'skip',
} as const;

export const CART_AUDIT_EVENTS = {
  CART_CREATED: 'CART_CREATED',
  CART_EMPTIED: 'CART_EMPTIED',
  CART_DELETED: 'CART_DELETED',
  CART_LONG_DURATION: 'CART_LONG_DURATION',
  ORDER_PLACED: 'ORDER_PLACED',
  SESSION_TERMINATED_WITH_ACTIVE_CART: 'SESSION_TERMINATED_WITH_ACTIVE_CART',
} as const;

export let cartCreatedAt: number | null = null;

// Used to suppress false CART_DELETED when cart resets after order placement
let lastOrderPlacedMid: string | null = null;

// CENTRALIZED LOGGER

type LogLevel = typeof LOG_LEVELS[keyof typeof LOG_LEVELS];

const LOG_PREFIX = '[CartAudit]';

export function logEvent(level: LogLevel, event: string, data?: any) {
  const message = `${LOG_PREFIX} [${level.toUpperCase()}] ${event}`;

  if (data !== undefined) {
    if (level === LOG_LEVELS.ERROR) console.error(message, data);
    else if (level === LOG_LEVELS.WARN) console.warn(message, data);
    else console.log(message, data);
  } else {
    if (level === LOG_LEVELS.ERROR) console.error(message);
    else if (level === LOG_LEVELS.WARN) console.warn(message);
    else console.log(message);
  }
}

// STATE SETTERS

export function setCartCreatedAt(value: number | null) {
  logEvent(LOG_LEVELS.INFO, 'setCartCreatedAt', value ? new Date(value).toISOString() : 'null');
  cartCreatedAt = value;
}

export function setLastOrderPlacedMid(mid: string | null) {
  logEvent(LOG_LEVELS.INFO, 'setLastOrderPlacedMid', mid);
  lastOrderPlacedMid = mid;
}

export function getLastOrderPlacedMid(): string | null {
  return lastOrderPlacedMid;
}

// META & FEATURE CHECK

function getCartAuditMeta() {
  const state = store.getState();
  const businessData = state?.businessStore?.businessData || {};
  const userData = state?.businessStore?.userData || {};
  const saleData = state?.saleDataStore?.saleData || {};
  const selectedChannel = state?.saleDataStore?.selectedChannelData || {};

  return {
    branchName: businessData.branchName || '',
    branchCode: businessData.branchCode || '',
    staffId: userData.userId || '',
    staffName: [userData.name?.firstName, userData.name?.lastName]
      .filter(Boolean).join(' ') || '',
    channelId: saleData.channelId || '',
    channelDefId: selectedChannel.channelDefId || '',
    channelName: saleData.channelName || '',
    saleId: saleData.mid || saleData.saleId || '',
  };
}

export function isCartAuditEnabled(): boolean {
  const state = store.getState();
  const businessData = state?.businessStore?.businessData || {};
  const accountFeatures = businessData?.accountFeatures;

  return (
    !!accountFeatures &&
    typeof accountFeatures === 'object' &&
    !!accountFeatures['cartEventLogging']
  );
}

// EVENT FIRING

export function fireCartAuditEvent(params: CartAuditEventParams) {
  const { eventName, cartId, cartValue, staffId, staffName, channelOverride, timestamp: providedTimestamp } = params;
  if (!isCartAuditEnabled()) {
    logEvent(LOG_LEVELS.SKIP, `${eventName} BLOCKED`, 'cartEventLogging feature OFF');
    return;
  }
  const meta = getCartAuditMeta();
  const timestamp = providedTimestamp || Date.now();
  const state = store.getState();
  const token = getLocalStorage(LOCALSTORAGE_KEYS.aToken) || state?.businessStore?.authToken || '';

  const baseUrl = process.env.REACT_APP_RISTA_BASE_API_URL;
  const electronAPI = (window as any).electronAPI;
  if (electronAPI?.cartAudit?.setToken) {
    electronAPI.cartAudit.setToken({ token, baseUrl });
  } else {
    (window as any).parent.postMessage({
      detail: {
        key: 'chrome.cartAudit',
        action: 'setToken',
        message: { token, baseUrl }
      }
    }, '*');
  }

  const record = {
    key: `${cartId}/${eventName}/${timestamp}`,
    cartId,
    event: eventName,
    token,
    branchName: meta.branchName,
    branchCode: meta.branchCode,
    staffId: staffId || meta.staffId,
    staffName: staffName || meta.staffName,
    cartValue,
    timestamp,
    channelId: channelOverride?.channelId || meta.channelId,
    channelDefId: channelOverride?.channelDefId || meta.channelDefId,
    channelName: channelOverride?.channelName || meta.channelName,
    saleId: cartId || '',
  };

  try {
    replace({
      domain: OFFLINE_DOMAINS.CART_AUDIT,
      values: { key: record.key, value: record },
    });
    logEvent(LOG_LEVELS.SUCCESS, eventName, { cartId, cartValue, staffId: record.staffId });
  } catch (err) {
    logEvent(LOG_LEVELS.ERROR, `${eventName} FAILED`, err);
  }
}

export function checkAndFireLongDuration(cartId: string, cartValue: number) {
  if (!cartCreatedAt) {
    logEvent(LOG_LEVELS.SKIP, CART_AUDIT_EVENTS.CART_LONG_DURATION, 'cartCreatedAt is null');
    return;
  }

  const threshold = 3 * 60 * 1000; // 3 minutes
  const duration = Date.now() - cartCreatedAt;

  if (duration > threshold) {
    logEvent(LOG_LEVELS.INFO, `${CART_AUDIT_EVENTS.CART_LONG_DURATION} threshold crossed`, `${Math.round(duration / 1000)}s`);
    fireCartAuditEvent({ eventName: CART_AUDIT_EVENTS.CART_LONG_DURATION, cartId, cartValue });
  } else {
    logEvent(LOG_LEVELS.SKIP, `${CART_AUDIT_EVENTS.CART_LONG_DURATION} below threshold`, `${Math.round(duration / 1000)}s`);
  }
}

// SESSION MANAGEMENT

export function startCartAuditSession(staffId: string) {
  if (!staffId) {
    logEvent(LOG_LEVELS.WARN, 'startCartAuditSession ABORTED', 'no staffId provided');
    return;
  }

  const existing = getLocalStorage(LOCALSTORAGE_KEYS.CART_AUDIT_SESSION);
  if (existing) {
    try {
      const prev = JSON.parse(existing);
      if (prev?.cartId && prev?.isCartActive) {
        logEvent(LOG_LEVELS.WARN, 'PREVIOUS CART WAS ACTIVE', `cartId: ${prev.cartId}`);
        // Use lastActiveAt (cart creation time) instead of startTime (session start time)
        // This ensures the event is reported on the date the cart was created, even if
        // the session terminates on a different business day
        fireCartAuditEvent({
          eventName: CART_AUDIT_EVENTS.SESSION_TERMINATED_WITH_ACTIVE_CART,
          cartId: prev.cartId,
          cartValue: prev.cartValue || 0,
          staffId: prev.staffId || '',
          staffName: prev.staffName || '',
          timestamp: prev.lastActiveAt || prev.startTime || prev.timestamp || undefined,
        });
      }
    } catch (e) {
      logEvent(LOG_LEVELS.ERROR, 'Failed to parse existing session', e);
    }
  }

  const newSession = {
    sessionId: `${staffId}_${Date.now()}`,
    staffId,
    cartId: null,
    cartValue: 0,
    isCartActive: false,
    lastActiveAt: null,  // Will be set when cart becomes active
    startTime: Date.now(),
  };

  setLocalStorage(LOCALSTORAGE_KEYS.CART_AUDIT_SESSION, JSON.stringify(newSession));
  logEvent(LOG_LEVELS.SUCCESS, 'New session created', { staffId });
}

export function updateCartAuditSession(cartId: string, cartValue: number, isCartActive: boolean) {
  const existing = getLocalStorage(LOCALSTORAGE_KEYS.CART_AUDIT_SESSION);
  if (!existing) {
    logEvent(LOG_LEVELS.SKIP, 'updateCartAuditSession', 'no active session');
    return;
  }

  const session = JSON.parse(existing);
  const meta = getCartAuditMeta();

  // Track when cart was first activated (or cart changed)
  let lastActiveAt = session.lastActiveAt || null;
  const cartChanged = session.cartId !== cartId;
  if (isCartActive) {
    if (!lastActiveAt || cartChanged) {
      // Set timestamp on first activation or when switching to a new cart
      lastActiveAt = Date.now();
      logEvent(LOG_LEVELS.INFO, `Cart activated: ${cartId} @ ${new Date(lastActiveAt).toISOString()}`);
    }
  }

  const updated = {
    ...session,
    cartId,
    cartValue,
    isCartActive,
    lastActiveAt,
    staffId: meta.staffId,
    staffName: meta.staffName,
  };

  setLocalStorage(LOCALSTORAGE_KEYS.CART_AUDIT_SESSION, JSON.stringify(updated));
  logEvent(LOG_LEVELS.INFO, 'Session updated', { cartId, cartValue, isCartActive, lastActiveAt: lastActiveAt ? new Date(lastActiveAt).toISOString() : null });
}

export function endCartAuditSession() {
  const existing = getLocalStorage(LOCALSTORAGE_KEYS.CART_AUDIT_SESSION);
  if (existing) {
    try {
      const prev = JSON.parse(existing);
      if (prev?.cartId && prev?.isCartActive) {
        logEvent(LOG_LEVELS.INFO, 'endCartAuditSession — cart active, preserving session for next login');
        return;
      }
    } catch (e) {
      logEvent(LOG_LEVELS.ERROR, 'endCartAuditSession — parse error', e);
    }
  }
  removeLocalStorage(LOCALSTORAGE_KEYS.CART_AUDIT_SESSION);
  logEvent(LOG_LEVELS.SUCCESS, 'Session ended');
}

// HIGH-LEVEL HELPERS

export function fireOrderPlacedEvent(cartId: string, cartValue: number) {
  setLastOrderPlacedMid(cartId);

  checkAndFireLongDuration(cartId, cartValue);
  fireCartAuditEvent({ eventName: CART_AUDIT_EVENTS.ORDER_PLACED, cartId, cartValue });

  updateCartAuditSession(cartId, cartValue, false);
  endCartAuditSession();

  setCartCreatedAt(null);
}